import { X } from "lucide-react";
import transparentBg from "../assets/images/transparent-bg.png";
import { useState } from "react";

interface AddNewCategoryModalProps {
    onClose: () => void
}

export function AddNewCategoryModal({
    onClose
}: AddNewCategoryModalProps) {

    const [publish, setPublish] = useState(true);
    const [customerReviews, setCustomerReviews] = useState(false);
    const [showOnHome, setShowOnHome] = useState(false);

    return (
        <section className="absolute inset-0 z-50" style={{ height: "100vh" }}>
            <div
                className="absolute inset-0 bg-cover bg-center flex justify-center px-2 pb-2 overflow-hidden overflow-y-auto"
                style={{
                    backgroundImage: `url(${transparentBg})`,
                }}
            >
                <div className="bg-white rounded-2xl w-full shadow-xl mt-5 px-4 md:px-8 h-fit lg:h-[764px] relative">
                    {/* Close button - top right for all screen sizes */}
                    <div className="absolute top-4 right-4 md:right-8 z-10">
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {/* Modal Content with Dynamic Divider */}
                    <div className="flex flex-col lg:flex-row relative pt-4">
                        {/* Left form Side */}
                        <div className="w-full lg:w-1/2 lg:pr-6 py-4">
                            <h2 className="text-lg font-medium text-emerald-600">Add New Category</h2>

                            {/* Form */}
                            <form className="w-full mt-4 flex flex-col justify-between gap-6">
                                <div className="flex flex-col gap-5">
                                    <div className="space-y-2">
                                        <label
                                            htmlFor="categoryName"
                                            className="block text-sm text-gray-600 font-medium"
                                        >
                                            Category Name
                                        </label>
                                        <input
                                            id="categoryName"
                                            name="categoryName"
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                                            placeholder="Enter Category Name"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="products"
                                            className="block text-sm text-gray-600 font-medium"
                                        >
                                            Products
                                        </label>
                                        <select
                                            name="products"
                                            id="products"
                                            className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                                        >
                                            <option value="">Select Product</option>
                                            <option value="">Product - 1</option>
                                            <option value="">Product - 2</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="discount"
                                            className="block text-sm text-gray-600 font-medium"
                                        >
                                            Discount %
                                        </label>
                                        <input
                                            id="discount"
                                            name="discount"
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                                            placeholder="Enter discount"
                                        />
                                    </div>

                                    {/* Toggle Switches */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-3 gap-3">
                                        {/* Toggle Block */}
                                        {[
                                            { label: "Publish", state: publish, setter: setPublish },
                                            { label: "Customer Reviews", state: customerReviews, setter: setCustomerReviews },
                                            { label: "Show on Home", state: showOnHome, setter: setShowOnHome },
                                        ].map(({ label, state, setter }) => (
                                            <div
                                                key={label}
                                                className="flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm"
                                            >
                                                <span className="text-sm text-gray-700 font-medium">{label}</span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={state}
                                                        onChange={() => setter(!state)}
                                                    />
                                                    <div className="w-10 h-5 bg-gray-300 peer-focus:ring-emerald-500 rounded-full peer-checked:bg-emerald-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:left-0.5 after:top-0.5 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Buttons Box */}
                                <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap mt-4">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed"
                                    >
                                        Add Category
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="w-full sm:w-auto px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Responsive divider container*/}
                        <div className="hidden lg:flex flex-col items-center self-stretch mx-2">
                            {/* Vertical divider that adjusts to content height */}
                            <div className="w-px bg-gray-300 h-[700px] my-4"></div>
                        </div>

                        {/* Mobile/Tablet divider */}
                        <div className="w-full h-px bg-gray-300 my-4 lg:hidden"></div>

                        {/* Right Products Side */}
                        <div className="w-full lg:w-1/2 lg:pl-4 py-2 lg:py-4">
                            <div className="flex justify-between items-center">
                                <h2 className="text-lg font-medium text-emerald-600">Products - 0</h2>
                            </div>

                            {/* Product list container */}
                            <div className="mt-4 h-56 md:h-64 lg:h-80 border border-gray-200 rounded-lg p-3 overflow-y-auto">
                                <p className="text-gray-500 text-sm text-center pt-10">No products selected</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}